import {
    createStyles,
    Theme,
    WithStyles,
    withStyles
} from '@material-ui/core/styles';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RootStore } from '../../stores/root.store';
import { AccountFull } from '../../models/account';
import AccountProvider from '../../providers/account.provider';
import { CCSpinner } from '../../shared/components/cc-spinner';
import { SimpleModal } from '../../shared/components/simple-modal';
import { PageBoundary } from '../../shared/components/simple-grid-pagination';
import { AccountPicker } from '../../components';
import { DialogResult, SimpleDialog } from '../../shared/components/simple-dialog';
import { Button, Typography } from '@material-ui/core';
import { AddOutlined, Error } from '@material-ui/icons';
import { Folder, Team } from '../../shared/domain';
import { Role, RoleDescription } from '../../models/role';
import { UserFull } from '../../models/user';
import { SimpleListPagination } from '../../shared/components/simple-list-pagination';
import { SimpleList, SimpleListItem } from '../../shared/components/simple-list';
import SchedulingProvider from '../../providers/scheduling.provider';
import TeamProvider from '../../providers/team.provider';
import UserProvider from '../../providers/user.provider';
import { ReportingRoleForm } from '../../components/reporting-role-form';
import { formIntent } from '../teams/teams';
import { RoleRegisteredChanges } from '../scheduling-permissions/scheduling-permissions';
import { UserReportingSearch } from '../../components/reporting-user-scheduling-search';

const ROWS_PER_PAGE = 10;
const ACCOUNTS_PAGING_LIMIT = 20;

const styles = (theme: Theme) =>
    createStyles({
        container: {
            flex: 1,
            display: 'flex',
            flexDirection: 'row'
        },
        errorPopup: {
            width: '98%',
            borderRadius: 5
        },
        errorPopupHeader: {
            height: '0.6em',
            backgroundColor: theme.palette.error.main,
            borderRadius: '3px 3px 0 0'
        },
        errorPopupContentContainer: {
            display: 'flex',
            flexDirection: 'row',
            padding: theme.spacing(1)
        },
        errorPopupIcon: {
            fontSize: 40,
            marginRight: '1em'
        },
        mainSpinnerLabel: {
            paddingBottom: '30px'
        },
        progressContainer: {
            flexGrow: 1
        },
        animContainer: {
            transition: 'width 1s'
        },
        formHolder: {
            marginTop: '1.5em'
        },
        heading: {
            fontWeight: 700,
            marginBottom: '0.5em'
        },
        ellipsis: {
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
        },
        listLoader: {
            height: '25em',
            background: 'lightgray',
            marginTop: '1em'
        },
        simpleList: {
            overflow: 'auto',
            height: '100%',
            minWidth: '25em',
            marginTop: '1.5em',
            '& .MuiTypography-displayBlock': {
                whiteSpace: 'break-spaces !important'
            }
        },
        modalSimpleList: {
            overflow: 'auto',
            height: '100%',
            maxHeight: '40em',
            minWidth: '25em',
            maxWidth: '40em',
            marginTop: '1.5em',
            '& .MuiTypography-displayBlock': {
                whiteSpace: 'break-spaces !important'
            }
        },
        resumePopup: {
            width: '41em',
            borderRadius: 5
        },
        resumePopupHeader: {
            height: '2em',
            backgroundColor: theme.palette.primary.main,
            borderRadius: '3px 3px 0 0',
            color: 'white',
            padding: '0.5em',
            fontSize: '1.5em'
        },
        popupContentContainer: {
            display: 'flex',
            flexDirection: 'column',
            padding: theme.spacing(1)
        },
        modalButtonContainer: {
            width: '100%',
            display: 'flex',
            flexDirection: 'row'
        },
        modalButton: {
            width: '49%',
            margin: '0 1em'
        }
    });

interface Props extends WithStyles<typeof styles> {
    rootStore: RootStore;
    onAuthError?: () => void;
}

interface States {
    accounts: AccountFull[];
    currentAccount: AccountFull | null;
    currentAccountOffset: number;
    networkError: boolean;
    dataPageIsLoading: boolean;
    isAccountsLoading: boolean;
    totalAccounts: number;
    searchAccountName: string;
    storedAccounts: AccountFull[];
    storedAccountOffset: number;
    storedAccountTotalCount: number;
    mainSpinnerText: string;
    isErrorModalOpened: boolean;
    errorModalHeader: string;
    errorModalText: string;
    accountFolders: Folder[];
    isFoldersLoading: boolean;
    isCreateRole: boolean;
    selectedRole: RoleDescription | null;
    roleToSubmit: RoleDescription | null;
    isConfirmChanges: boolean;
    searchedUser: UserFull | null;
    searchedUserRoles: Role[];
    isUserSearchLoading: boolean;
    accountRoles: RoleDescription[];
    isConfirmRoleAddModalOpen: boolean;
    formHasChanges: boolean;
    tempRole: SimpleListItem | null;
    isChangesModalOpen: boolean;
    isRolesLoading: boolean;
    rolePermissions: string[];
    accountTeams: Team[];
    isAccountTeamsLoading: boolean;
    accountUsers: UserFull[];
    isUserSearchWarning: boolean;
    userSearchWarning: string;
    isFormSuccess: boolean;
    formIntent: formIntent;
    changeList: SimpleListItem[];
    isValidAccount: boolean;
}

@inject('rootStore')
@observer
class ReportingPermissions extends React.Component<Props, States> {
    public static defaultProps: Partial<Props> = {};

    state: States = {
        accounts: [],
        currentAccount: null,
        currentAccountOffset: 0,
        networkError: false,
        dataPageIsLoading: true,
        isAccountsLoading: false,
        totalAccounts: 0,
        searchAccountName: '',
        mainSpinnerText: 'Please wait. Loading data.',
        storedAccounts: [],
        storedAccountOffset: 0,
        storedAccountTotalCount: 0,
        isErrorModalOpened: false,
        errorModalHeader: '',
        errorModalText: '',
        accountFolders: [],
        isFoldersLoading: false,
        isCreateRole: false,
        selectedRole: null,
        roleToSubmit: null,
        isConfirmChanges: false,
        searchedUser: null,
        searchedUserRoles: [],
        isUserSearchLoading: false,
        accountRoles: [],
        isConfirmRoleAddModalOpen: false,
        formHasChanges: false,
        tempRole: null,
        isChangesModalOpen: false,
        isRolesLoading: false,
        rolePermissions: [],
        accountTeams: [],
        isAccountTeamsLoading: false,
        accountUsers: [],
        isUserSearchWarning: false,
        userSearchWarning: '',
        isFormSuccess: false,
        formIntent: 'EDIT',
        changeList: [],
        isValidAccount: false
    };

    schedulingProvider = new SchedulingProvider();
    accountProvider = new AccountProvider();
    teamProvider = new TeamProvider();
    userProvider = new UserProvider();

    componentDidMount() {
        this.fetchAccounts(this.state.currentAccountOffset);
    }

    fetchAccounts = (offset: number, limit: number = ACCOUNTS_PAGING_LIMIT) => {
        return this.accountProvider
            .getAllAccountsFullSchema(offset, limit)
            .then(results => {
                const { accounts, total_account } = results;
                if (!accounts) {
                    if (offset === 0) {
                        this.setState({ networkError: true });
                    }
                    return;
                }

                this.setState({
                    isAccountsLoading: false,
                    totalAccounts: total_account,
                    accounts,
                    currentAccountOffset: offset,
                    dataPageIsLoading: false
                });
            })
            .catch(error => {
                console.error(error);
                const { status } = error.response;
                if (status === 401) {
                    const { onAuthError } = this.props;
                    if (!onAuthError) {
                        return;
                    }
                    onAuthError();
                    return;
                }
                this.setState({ networkError: true });
            });
    };

    onAccountDataPageOverBoundaryReached = (
        boundary: PageBoundary,
        nextPage: number
    ): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            const {
                currentAccountOffset,
                totalAccounts,
                searchAccountName
            } = this.state;
            const isFirstPage = nextPage === 0;
            const isLastPage =
                nextPage === Math.ceil(totalAccounts / ROWS_PER_PAGE) - 1;
            const newOffset = isFirstPage
                ? 0
                : isLastPage
                ? ACCOUNTS_PAGING_LIMIT *
                  Math.floor(totalAccounts / ACCOUNTS_PAGING_LIMIT)
                : boundary === PageBoundary.Upper
                ? currentAccountOffset + ACCOUNTS_PAGING_LIMIT
                : currentAccountOffset - ACCOUNTS_PAGING_LIMIT;
            this.setState({ isAccountsLoading: true });
            if (searchAccountName !== '') {
                this.searchAccounts(searchAccountName, newOffset);
                return;
            }
            this.fetchAccounts(newOffset)
                .then(() => resolve())
                .catch(() => reject());
        });
    };

    searchAccounts = (searchName: string, offset?: number) => {
        // Nothing to search load the normal account list
        if (searchName === '') {
            const {
                storedAccounts,
                storedAccountOffset,
                storedAccountTotalCount
            } = this.state;

            // Empty search do nothing
            if (!storedAccounts.length) {
                return;
            }

            // Going back from actual seach
            this.setState({
                accounts: storedAccounts,
                currentAccountOffset: storedAccountOffset,
                totalAccounts: storedAccountTotalCount,
                searchAccountName: '',
                storedAccounts: [],
                storedAccountOffset: 0,
                storedAccountTotalCount: 0
            });
            return;
        }

        // We have a name search
        const {
            currentAccountOffset,
            accounts,
            totalAccounts,
            storedAccounts
        } = this.state;
        this.setState(
            {
                isAccountsLoading: true,
                searchAccountName: searchName
            },
            () => {
                const searchOffset = offset ? offset : 0;
                return this.accountProvider
                    .searchAccountByNameFullSchema(
                        searchName,
                        searchOffset,
                        ACCOUNTS_PAGING_LIMIT
                    )
                    .then(results => {
                        const {
                            accounts: foundAccounts,
                            total_account
                        } = results;
                        if (!foundAccounts) {
                            if (currentAccountOffset === 0) {
                                this.setState({ networkError: true });
                            }
                            return;
                        }

                        // If we haven't stored the original list do it
                        // This prevents multiple searchs overriding each others
                        if (!storedAccounts.length) {
                            this.setState({
                                storedAccounts: accounts,
                                storedAccountTotalCount: totalAccounts,
                                storedAccountOffset: currentAccountOffset
                            });
                        }

                        this.setState({
                            isAccountsLoading: false,
                            totalAccounts: total_account,
                            accounts: foundAccounts,
                            currentAccountOffset: searchOffset
                        });
                    })
                    .catch(error => {
                        const { status } = error.response;
                        if (status === 401) {
                            const { onAuthError } = this.props;
                            if (!onAuthError) {
                                return;
                            }
                            onAuthError();
                            return;
                        }
                        this.setState({ networkError: true });
                    });
            }
        );
    };

    onAccountClicked = (account: AccountFull) => {
        const { currentAccount } = this.state;
        if (currentAccount === account) {
            return;
        }

        let rolePermissions: string[] = [];

        this.accountProvider.fetchAccountMeta(account.account_id)
            .then((results) => {
                const { account: accountMeta } = results;

                this.setState({
                    currentAccount: accountMeta
                });
                
                if (
                    accountMeta.meta['reporting'] &&
                    accountMeta.meta['reporting']['available_reporting']
                ) {
                    rolePermissions = Object.keys(
                        accountMeta.meta['reporting']['available_reporting']
                    ).map(e =>
                        e
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, c => c.toUpperCase())
                    );
                } else {
                    this.setState({
                        isValidAccount: false,
                        isRolesLoading: false,
                        isFoldersLoading: false
                    });
                    return;
                }
        
                this.setState(
                    {
                        isRolesLoading: true,
                        isFoldersLoading: true,
                        roleToSubmit: null,
                        selectedRole: null,
                        formHasChanges: false,
                        rolePermissions,
                        isAccountTeamsLoading: true,
                        isValidAccount: true
                    },
                    () => {
                        this.fetchAccountFolders(accountMeta);
                        this.fetchAccountRoles(accountMeta);
                        this.fetchAccountsTeams(accountMeta.account_id);
                    }
                );

            }).catch((error) => {
                // tslint:disable-next-line:no-console
                console.error(error);
                const { status } = error.response;
                if (status === 401) {
                    const { onAuthError } = this.props;
                    if (!onAuthError) {
                        return;
                    }
                    onAuthError();
                    return;
                }
                this.setState({
                    isErrorModalOpened: true,
                    errorModalHeader: 'Error fetching account',
                    errorModalText: error.message
                });
            });

    };

    fetchAccountFolders = (account: AccountFull) => {
        this.accountProvider
            .getFoldersByAccountId(account.account_id)
            .then(data => {
                this.setState({
                    accountFolders: data,
                    isFoldersLoading: false
                });
            })
            .catch(error => {
                const { status } = error.response;
                if (status === 401) {
                    const { onAuthError } = this.props;
                    if (!onAuthError) {
                        return;
                    }
                    onAuthError();
                    return;
                }
                this.setState({
                    isErrorModalOpened: true,
                    errorModalHeader: 'Error fetching accounts',
                    errorModalText: error.message
                });
            });
    };

    fetchAccountRoles = (account: AccountFull) => {
        this.accountProvider
            .fetchAccountRoles(account.account_id, 'reporting')
            .then(data => {
                this.setState({
                    accountRoles: data.roles,
                    isRolesLoading: false
                });
            })
            .catch(error => {
                const { status } = error.response;
                if (status === 401) {
                    const { onAuthError } = this.props;
                    if (!onAuthError) {
                        return;
                    }
                    onAuthError();
                    return;
                }
                this.setState({
                    isErrorModalOpened: true,
                    errorModalHeader: 'Error fetching accounts',
                    errorModalText: error.message
                });
            });
    };

    fetchAccountsTeams = (accountId: string) => {
        this.accountProvider.fetchTeamsFromAccountId(accountId).then(
            data => {
                const { teams } = data;
                this.setState({
                    isAccountTeamsLoading: false,
                    accountTeams: teams
                });
            },
            error => {
                console.error(error);
                const { status } = error.response;
                if (status === 401) {
                    const { onAuthError } = this.props;
                    if (!onAuthError) {
                        return;
                    }
                    onAuthError();
                    return;
                }
                this.setState({ networkError: true });
            }
        );
    };

    fetchAccountTeamAvailableUsers = (account: AccountFull) => {
        this.setState({ isAccountTeamsLoading: true });
        let teamId = '';
        try {
            teamId =
                account.meta['scheduling_routes']['admin_tool'][
                    'permissions_users_lookup_team'
                ];
            this.teamProvider
                .fetchTeamUsers(teamId)
                .then(data => {
                    const { users } = data;
                    this.setState({
                        accountUsers: users,
                        isAccountTeamsLoading: false
                    });
                })
                .catch(error => {
                    const { status } = error.response;
                    if (status === 401) {
                        const { onAuthError } = this.props;
                        if (!onAuthError) {
                            return;
                        }
                        onAuthError();
                        return;
                    }
                    this.setState({
                        isErrorModalOpened: true,
                        isAccountTeamsLoading: false,
                        errorModalHeader: 'Error fetching account users',
                        errorModalText: error.message
                    });
                });
        } catch (error) {
            this.setState({
                isErrorModalOpened: true,
                isAccountTeamsLoading: false,
                errorModalHeader: 'Error fetching account users',
                errorModalText: "Account doesn't have PCL team set up"
            });
        }
    };

    onErrorModalClicked = () => {
        this.setState({ isErrorModalOpened: false });
    };

    onCreateRolClicked = () => {
        const { currentAccount } = this.state;

        if (!currentAccount) {
            return;
        }

        this.setState(
            {
                isCreateRole: true,
                selectedRole: null,
                roleToSubmit: {} as RoleDescription,
                isConfirmChanges: false,
                searchedUser: null,
                isUserSearchLoading: false,
                searchedUserRoles: []
            },
            () => {
                this.setState({
                    selectedRole: {
                        name: '',
                        id: 0,
                        permissions: [],
                        permissions_list: [],
                        users: [],
                        folder_ids: [],
                        account_doc_id: currentAccount._id,
                        members: [],
                        module_group_ids: [],
                        type: 'reporting',
                        alerting_list: [],
                        alert_records: []
                    }
                });
            }
        );
    };

    onRoleClick = (listItem: SimpleListItem) => {
        const { accountRoles } = this.state;
        const { formHasChanges } = this.state;

        if (!accountRoles) {
            return;
        }

        const selectedRole = accountRoles.find(
            e => e.id === Number.parseInt(listItem.id)
        );

        if (!selectedRole) {
            return;
        }

        if (!formHasChanges) {
            const { searchedUser } = this.state;

            if (searchedUser) {
                this.setState({
                    roleToSubmit: selectedRole,
                    isCreateRole: false,
                    selectedRole: null,
                    isConfirmRoleAddModalOpen: true
                });
                return;
            }
            this.setState(
                {
                    isCreateRole: false,
                    selectedRole: null
                },
                () => {
                    this.setState({ selectedRole });
                }
            );
            return;
        }

        const { tempRole } = this.state;

        if (tempRole && tempRole.id === listItem.id) {
            return;
        }

        this.setState({
            tempRole: listItem,
            isChangesModalOpen: true
        });
    };

    searchUserById = (userId: string) => {
        const { accountUsers } = this.state;
        this.userProvider
            .fetchFullUserById(userId)
            .then(data => {
                const { user } = data;
                accountUsers.push(user);

                this.setState({ accountUsers });
            })
            .catch(error => {
                console.error(error);
                const { status } = error.response;
                if (status === 401) {
                    const { onAuthError } = this.props;
                    if (!onAuthError) {
                        return undefined;
                    }
                    onAuthError();
                    return;
                }
                return;
            });
    };

    setFormChanges = () => {
        this.setState({ formHasChanges: true });
    };

    onCloseForm = () => {
        this.setState({ selectedRole: null });
    };

    onUserEmailSearch = (email: string) => {
        const { currentAccount } = this.state;

        if (!email || email === '') {
            return;
        }

        if (!currentAccount) {
            return;
        }

        this.setState({ isUserSearchLoading: true });

        this.schedulingProvider
            .fetchUserByEmailAndAccountId(
                email,
                currentAccount.account_id,
                'reporting'
            )
            .then(data => {
                const { user, roles } = data;
                this.setState({
                    searchedUser: user,
                    searchedUserRoles: roles,
                    isUserSearchWarning: false,
                    userSearchWarning: ''
                });
            })
            .catch(error => {
                console.error(error);
                const { status } = error.response;
                if (status === 401) {
                    const { onAuthError } = this.props;
                    if (!onAuthError) {
                        return;
                    }
                    onAuthError();
                    return;
                }

                let userSearchWarning = '';
                if (status === 403) {
                    userSearchWarning = 'User DOES NOT belong to this customer';
                } else if (status === 404) {
                    userSearchWarning = 'User not found';
                } else {
                    userSearchWarning = 'Error fetching use';
                }
                this.setState({
                    isUserSearchWarning: true,
                    userSearchWarning,
                    searchedUserRoles: [],
                    searchedUser: null
                });
            })
            .finally(() => {
                this.setState({ isUserSearchLoading: false });
            });
    };

    onUserRoleRemove = (roleId: string) => {
        const { currentAccount, searchedUser, accountRoles } = this.state;

        if (!currentAccount || !searchedUser) {
            return;
        }

        const roleToRemove = accountRoles.find(e => `${e.id}` === roleId);

        if (!roleToRemove) {
            return;
        }

        this.setState({ isUserSearchLoading: true });
        this.userProvider
            .removeUserFromRole(searchedUser._id, `${roleToRemove.id}`, 'reporting')
            .then(data => {
                const { user, roles } = data;
                this.setState(
                    {
                        isFormSuccess: true,
                        formIntent: 'EDIT',
                        searchedUser: user,
                        searchedUserRoles: roles,
                        isUserSearchWarning: false,
                        userSearchWarning: ''
                    },
                    () => {
                        this.fetchAccountRoles(currentAccount);
                    }
                );
            })
            .catch(error => {
                console.error(error);
                const { status, data } = error.response;
                if (status === 401) {
                    const { onAuthError } = this.props;
                    if (!onAuthError) {
                        return;
                    }
                    onAuthError();
                    return;
                }
                this.setState({
                    isErrorModalOpened: true,
                    errorModalHeader: 'Error on rol update',
                    errorModalText: data
                        ? `${data.error}, ${data.message}`
                        : error
                });
            })
            .finally(() => {
                this.setState({
                    isUserSearchLoading: false,
                    roleToSubmit: null
                });
            });
    };

    onUserClear = () => {
        const { searchedUser } = this.state;

        if (!searchedUser) {
            return;
        }

        this.setState({
            searchedUser: null
        });
    };

    onSubmitChanges = (
        roleInForm: RoleDescription,
        changeList: RoleRegisteredChanges[],
        intent: formIntent
    ) => {
        const changeItemList = changeList.map(e => {
            return new SimpleListItem(e.field, e.label, e.value);
        });

        this.setState({
            roleToSubmit: roleInForm,
            formIntent: intent,
            changeList: changeItemList,
            isConfirmChanges: true
        });
    };

    closeResumeModal = () => {
        this.setState({ isConfirmChanges: false, changeList: [] });
    };

    onSubmitRole = () => {
        const { formIntent, roleToSubmit } = this.state;

        if (!roleToSubmit) {
            return;
        }

        this.setState(
            {
                isConfirmChanges: false,
                selectedRole: null,
                isCreateRole: false,
                formHasChanges: false
            },
            () => {
                if (formIntent === 'CREATE') {
                    this.onCreateSubmit(roleToSubmit);
                } else {
                    this.onEditSubmit(roleToSubmit);
                }
            }
        );
    };

    onCreateSubmit = (newRole: RoleDescription) => {
        this.setState({
            dataPageIsLoading: true,
            mainSpinnerText: 'Creating Role',
            changeList: []
        });
        this.schedulingProvider
            .createSchedulingRole(newRole, 'reporting')
            .then(data => {
                const { currentAccount } = this.state;

                if (!currentAccount) {
                    return;
                }

                this.setState(
                    {
                        isFormSuccess: true,
                        isRolesLoading: true
                    },
                    () => {
                        this.fetchAccountRoles(currentAccount);
                    }
                );

                return;
            })
            .catch(error => {
                console.error(error);
                const { status, data } = error.response;
                if (status === 401) {
                    const { onAuthError } = this.props;
                    if (!onAuthError) {
                        return;
                    }
                    onAuthError();
                    return;
                }
                this.setState({
                    isErrorModalOpened: true,
                    errorModalHeader: 'Error on rol creation',
                    errorModalText: data
                        ? `${data.error}, ${data.message}`
                        : error
                });
            })
            .finally(() => {
                this.setState({ dataPageIsLoading: false });
            });
    };

    onEditSubmit = (updateRole: RoleDescription) => {
        this.setState({
            dataPageIsLoading: true,
            mainSpinnerText: 'Updating Role',
            changeList: []
        });
        this.schedulingProvider
            .updateShedulingRole(updateRole, 'reporting')
            .then(data => {
                const { currentAccount } = this.state;

                if (!currentAccount) {
                    return;
                }

                this.setState(
                    {
                        isFormSuccess: true,
                        isRolesLoading: true
                    },
                    () => {
                        this.fetchAccountRoles(currentAccount);
                    }
                );

                return;
            })
            .catch(error => {
                console.error(error);
                const { status, data } = error.response;
                if (status === 401) {
                    const { onAuthError } = this.props;
                    if (!onAuthError) {
                        return;
                    }
                    onAuthError();
                    return;
                }
                this.setState({
                    isErrorModalOpened: true,
                    errorModalHeader: 'Error on rol update',
                    errorModalText: data
                        ? `${data.error}, ${data.message}`
                        : error
                });
            })
            .finally(() => {
                this.setState({ dataPageIsLoading: false });
            });
    };

    onChangesModalClicked = (dialogResult: DialogResult) => {
        if (!dialogResult) {
            return;
        }

        if (dialogResult === DialogResult.Ok) {
            const { tempRole } = this.state;
            if (tempRole) {
                this.setState({
                    formHasChanges: false
                }, () => {
                    this.onRoleClick(tempRole);
                });
            }
        }

        this.setState({isChangesModalOpen: false});
    };

    onRoleAddToUserClicked = (dialogResult: DialogResult) => {
        if (!dialogResult) {
            return;
        }

        if (dialogResult === DialogResult.Cancel) {
            this.setState({
                isConfirmRoleAddModalOpen: false,
                roleToSubmit: null,
            });
            return;
        }

        const { currentAccount, searchedUser, roleToSubmit } = this.state;

        if (!currentAccount || !searchedUser || !roleToSubmit) {
            return;
        }

        this.setState({isUserSearchLoading: true})
        this.userProvider.addUserToRole(searchedUser._id, `${roleToSubmit.id}`, 'reporting').then((data) => {
            const { user, roles } = data;
            this.setState(
                {
                    isFormSuccess: true,
                    formIntent: 'EDIT',
                    searchedUser: user,
                    searchedUserRoles: roles,
                    isUserSearchWarning: false,
                    userSearchWarning: ''
                },
                () => {
                    this.fetchAccountRoles(currentAccount);
                }
            );
        }).catch(error => {
            console.error(error);
            const { status, data } = error.response;
            if (status === 401) {
                const { onAuthError } = this.props;
                if (!onAuthError) {
                    return;
                }
                onAuthError();
                return;
            }
            this.setState({
                isConfirmRoleAddModalOpen: false,
                roleToSubmit: null,
                isErrorModalOpened: true,
                errorModalHeader: 'Error on rol update',
                errorModalText: data ? `${data.error}, ${data.message}` : error
            });
        })
        .finally(() => {
            this.setState({
                isUserSearchLoading: false,
                roleToSubmit: null
            });
        });
    };

    onSuccessModalClick = () => {
        this.setState({
            isFormSuccess: false,
            selectedRole: null,
            formIntent: 'CREATE'
        });
    };

    public render() {
        const { classes, onAuthError } = this.props;
        const {
            accounts,
            currentAccount,
            currentAccountOffset,
            networkError,
            dataPageIsLoading,
            mainSpinnerText,
            isAccountsLoading,
            totalAccounts,
            isErrorModalOpened,
            errorModalHeader,
            errorModalText,
            isRolesLoading,
            accountRoles,
            selectedRole,
            accountTeams,
            isAccountTeamsLoading,
            accountFolders,
            isCreateRole,
            rolePermissions,
            searchedUser,
            searchedUserRoles,
            isUserSearchLoading,
            isUserSearchWarning,
            userSearchWarning,
            isConfirmChanges,
            changeList,
            isChangesModalOpen,
            roleToSubmit,
            isConfirmRoleAddModalOpen,
            isValidAccount,
            isFormSuccess,
            formIntent
        } = this.state;

        const currentAccountName = currentAccount ? currentAccount.name : '';
        const roleList = currentAccount
            ? accountRoles.map(e => {
                  return new SimpleListItem(
                      `${e.id}`,
                      e.name,
                      `ID: ${e.id} - Folders: ${e.folder_ids.length}`
                  );
              })
            : [];

        return (
            <div className={classes.container} data-testid="mainRender">
                <CCSpinner
                    label={mainSpinnerText}
                    labelClassName={classes.mainSpinnerLabel}
                    className={classes.progressContainer}
                    loading={dataPageIsLoading}
                    size={200}
                >
                    <AccountPicker
                        className={`${classes.animContainer}`}
                        rowsPerPage={ROWS_PER_PAGE}
                        accounts={accounts}
                        accountsIsLoading={isAccountsLoading}
                        accountsTotalItems={totalAccounts}
                        accountsItemsOffset={currentAccountOffset}
                        currentAccountName={currentAccountName}
                        onAccountClick={this.onAccountClicked}
                        onAccountDataPageOverBoundary={
                            this.onAccountDataPageOverBoundaryReached
                        }
                        onAccountSearch={this.searchAccounts}
                    />
                    {currentAccount && (
                        isValidAccount ? 
                        <div className={classes.formHolder}>
                            <Typography
                                variant="h6"
                                className={`${classes.heading} ${classes.ellipsis}`}
                            >
                                Reporting Permissions Roles on {currentAccountName}
                            </Typography>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<AddOutlined />}
                                onClick={this.onCreateRolClicked}
                                data-testid="create-role-button"
                            >
                                Create new Role
                            </Button>
                            <CCSpinner
                                loading={isRolesLoading}
                                className={classes.listLoader}
                            >
                                <div>
                                    <SimpleListPagination
                                        className={classes.simpleList}
                                        keepItemSelected={false}
                                        items={roleList}
                                        onListItemClick={this.onRoleClick}
                                        rowsPerPage={ROWS_PER_PAGE}
                                        totalItems={roleList.length}
                                        offset={0}
                                        noItemsLabel="No Roles found for this account"
                                    />
                                </div>
                            </CCSpinner>
                        </div>
                        :
                        <div className={classes.formHolder}>
                            <SimpleList
                                className={classes.simpleList}
                                items={[]}
                                noItemsLabel="This account has no valid configuration for reporting permissions"
                            />
                        </div>
                    )}
                    {selectedRole && currentAccount && (
                        <ReportingRoleForm
                            currentAccount={currentAccount}
                            accountTeams={accountTeams}
                            isAccountTeamsLoading={isAccountTeamsLoading}
                            accountFolders={accountFolders}
                            isCreate={isCreateRole}
                            rolePermissions={rolePermissions}
                            pickedRole={selectedRole}
                            searchUser={this.searchUserById}
                            setFormChanges={this.setFormChanges}
                            onCloseForm={this.onCloseForm}
                            teamProvider={this.teamProvider}
                            onAuthError={onAuthError}
                            onFormSubmit={this.onSubmitChanges}
                        />
                    )}
                    {!selectedRole && currentAccount && isValidAccount && (
                        <UserReportingSearch
                            selectedAccount={currentAccount}
                            searchedUser={searchedUser}
                            searchedUserRoles={searchedUserRoles}
                            userIsLoading={isUserSearchLoading}
                            isUserSearchWarning={isUserSearchWarning}
                            userSearchWarning={userSearchWarning}
                            onUserSearch={this.onUserEmailSearch}
                            onRoleRemove={this.onUserRoleRemove}
                            onUserClear={this.onUserClear}
                        />
                    )}
                </CCSpinner>
                <SimpleDialog
                    open={isErrorModalOpened}
                    titleText={errorModalHeader}
                    contentText={errorModalText}
                    buttonCancelLabel=""
                    onDialogResult={this.onErrorModalClicked}
                />
                <SimpleModal
                    className={classes.resumePopup}
                    open={isConfirmChanges}
                    contentClasses={classes.popupContentContainer}
                    buttonOkLabel=""
                    buttonCancelLabel=""
                    header="Role changes resume"
                    headerClassName={classes.resumePopupHeader}
                >
                    <Typography variant="subtitle1">
                        Please review the role changes before submitting, once
                        you are sure they are correct click on the submit button
                    </Typography>
                    <SimpleListPagination
                        className={classes.modalSimpleList}
                        keepItemSelected={true}
                        items={changeList}
                        isLoading={false}
                        rowsPerPage={ROWS_PER_PAGE}
                        totalItems={changeList.length}
                        offset={0}
                    />
                    <div className={classes.modalButtonContainer}>
                        <Button
                            variant="contained"
                            color="secondary"
                            className={classes.modalButton}
                            onClick={this.closeResumeModal}
                            data-testid="cancel-changes-button"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.modalButton}
                            onClick={this.onSubmitRole}
                            data-testid="submit-changes-button"
                        >
                            Submit
                        </Button>
                    </div>
                </SimpleModal>
                <SimpleDialog
                    open={isChangesModalOpen}
                    titleText="You have unsaved changes"
                    contentText="Do you wish to discard them?"
                    buttonCancelLabel="No"
                    buttonOkLabel="Yes"
                    onDialogResult={this.onChangesModalClicked}
                />
                {roleToSubmit && isConfirmRoleAddModalOpen && searchedUser && (
                    <SimpleDialog
                        open={isConfirmRoleAddModalOpen}
                        titleText="Add Role to User"
                        contentText={`Are you sure you want to add ${roleToSubmit.name} to ${searchedUser.email}?`}
                        buttonCancelLabel="No"
                        buttonOkLabel="Yes"
                        onDialogResult={this.onRoleAddToUserClicked}
                    />
                )}
                <SimpleDialog
                    open={isFormSuccess}
                    titleText="Success"
                    contentText={
                        formIntent === 'CREATE'
                            ? 'Role Created Succesfully'
                            : 'Role Updated Succesfully'
                    }
                    buttonCancelLabel=""
                    onDialogResult={this.onSuccessModalClick}
                />
                {networkError && (
                    <SimpleModal
                        className={classes.errorPopup}
                        open={networkError}
                        contentClasses={classes.errorPopupContentContainer}
                        buttonOkLabel=""
                        buttonCancelLabel=""
                        header=""
                        headerClassName={classes.errorPopupHeader}
                    >
                        <Error
                            color="error"
                            className={classes.errorPopupIcon}
                        />
                        <div>
                            <Typography variant="h5">
                                {'Network Error'}
                            </Typography>
                            <Typography variant="subtitle1">
                                {'Please reload the page.'}
                            </Typography>
                        </div>
                    </SimpleModal>
                )}
            </div>
        );
    }
}

const MUIComponent = withStyles(styles)(ReportingPermissions);
export { MUIComponent as ReportingPermissions };
